import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { ApiError, NetworkError } from "../api/client";
import { cancelPayment, createPayment, getMemberFinancialOverview, recordCashPayment, replacePayment, type AssistedPaymentResponse, type FinancialInvoice, type FinancialOverview } from "../api/payments";

export type MemberFinanceOperationsProps = {
  memberId: string;
  accessToken: string;
  capabilities: { readPayments: boolean; recordPayment: boolean; adjustPayment: boolean };
  onSessionExpired(): void;
};

type Action = { kind: "create" } | { kind: "cash" | "cancel" | "replace"; invoice: FinancialInvoice };
type FormValues = { competence: string; dueDate: string; method: "PIX" | "BOLETO"; justification: string };
type Feedback = { tone: "error" | "success" | "warning"; message: string; mfa?: boolean };

function amountLabel(amount: string | number): string {
  return Number(amount).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function invoiceLabel(invoice: FinancialInvoice): string {
  return invoice.kind === "monthly_fee" ? `Mensalidade · ${invoice.competencia?.slice(0, 7) ?? "Sem competência"}` : "Doação institucional";
}

function statusLabel(invoice: FinancialInvoice): string {
  return ({ pending: "Pendente", overdue: "Em atraso", paid: "Pago", cancelled: "Cancelada" })[invoice.status] ?? "Indisponível";
}

function isActive(invoice: FinancialInvoice): boolean {
  return invoice.status === "pending" || invoice.status === "overdue";
}

function isOperable(invoice: FinancialInvoice): boolean {
  return invoice.kind === "monthly_fee" && isActive(invoice);
}

function mayPerform(action: Action, capabilities: MemberFinanceOperationsProps["capabilities"]): boolean {
  if (action.kind === "create" || action.kind === "cash") return capabilities.recordPayment;
  return capabilities.adjustPayment;
}

function defaultCompetence(): string {
  const date = new Date();
  date.setMonth(date.getMonth() + 1, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

const actionTitles = { create: "Gerar cobrança digital", cash: "Receber no balcão", cancel: "Cancelar cobrança", replace: "Substituir cobrança" };
const confirmationTitles = { create: "Confirmar emissão", cash: "Confirmar recebimento", cancel: "Confirmar cancelamento", replace: "Confirmar substituição" };

function failureFeedback(error: unknown): Feedback {
  if (error instanceof NetworkError) return { tone: "warning", message: "Operação não confirmada por falha de conexão. Não reenvie: consulte o histórico e solicite conferência do protocolo." };
  if (!(error instanceof ApiError)) return { tone: "error", message: "Não foi possível confirmar a operação. Não reenvie antes da conferência do histórico." };
  if (error.code === "mfa_elevation_required") return { tone: "warning", message: "Confirme sua autenticação com MFA recente antes de uma nova operação.", mfa: true };
  const messages: Record<number, string> = {
    400: "Revise os campos informados. A operação não foi aceita pelo servidor.",
    403: "Sem permissão para executar esta operação financeira.",
    409: "Conflito no estado da cobrança. Atualize o histórico antes de decidir uma nova operação.",
    502: "Reconciliação necessária. Não reenvie a operação: solicite a conferência do histórico e do protocolo.",
  };
  return { tone: "error", message: messages[error.status] ?? "Operação não confirmada. Não reenvie antes da conferência do histórico." };
}

function FinancialLedger({ overview, capabilities, disabled, onAction }: {
  overview: FinancialOverview;
  capabilities: MemberFinanceOperationsProps["capabilities"];
  disabled: boolean;
  onAction(action: Action): void;
}) {
  return <section className="member-finance__ledger" aria-labelledby="finance-ledger-title">
    <h2 id="finance-ledger-title">Histórico financeiro</h2>
    {overview.invoices.length === 0 ? <p className="member-finance__empty">Nenhuma cobrança registrada para este associado.</p> : <ul className="member-finance__invoices">
      {overview.invoices.map((invoice) => <li className="member-finance__invoice" key={invoice.id}>
        <div className="member-finance__invoice-summary"><div><h3>{invoiceLabel(invoice)}</h3><p>Vencimento {new Date(`${invoice.due_date}T12:00:00`).toLocaleDateString("pt-BR")}</p></div><div className="member-finance__amount"><strong>{amountLabel(invoice.amount)}</strong><span>{statusLabel(invoice)}</span></div></div>
        {!disabled && isOperable(invoice) && <div className="member-finance__actions">
          {capabilities.recordPayment && <button className="btn btn--primary" type="button" onClick={() => onAction({ kind: "cash", invoice })}>Receber no balcão</button>}
          {capabilities.adjustPayment && <><button className="btn btn--secondary" type="button" onClick={() => onAction({ kind: "replace", invoice })}>Substituir cobrança</button><button className="btn btn--secondary" type="button" onClick={() => onAction({ kind: "cancel", invoice })}>Cancelar cobrança</button></>}
        </div>}
      </li>)}
    </ul>}
    {overview.receipts.length > 0 && <section className="member-finance__receipts" aria-labelledby="finance-receipts-title"><h3 id="finance-receipts-title">Recibos confirmados</h3><ul>{overview.receipts.map((receipt) => <li key={receipt.id}><strong>{receipt.receipt_reference}</strong><span>{amountLabel(receipt.amount)} · {new Date(receipt.received_at).toLocaleDateString("pt-BR")}</span></li>)}</ul></section>}
  </section>;
}

function DigitalFields({ values, onChange, creating }: { values: FormValues; onChange(value: FormValues): void; creating: boolean }) {
  return <div className="member-finance__fields">
    {creating && <label className="field"><span className="field__label">Competência</span><input className="field__input" type="month" required value={values.competence} onChange={(event) => onChange({ ...values, competence: event.target.value, dueDate: `${event.target.value}-10` })} /></label>}
    <label className="field"><span className="field__label">Vencimento</span><input className="field__input" type="date" required value={values.dueDate} onChange={(event) => onChange({ ...values, dueDate: event.target.value })} /></label>
    <label className="field"><span className="field__label">Método de cobrança</span><select className="field__input" value={values.method} onChange={(event) => onChange({ ...values, method: event.target.value as FormValues["method"] })}><option value="PIX">PIX</option><option value="BOLETO">Boleto</option></select></label>
  </div>;
}

function confirmationLabel(action: Action): string {
  if (action.kind === "cash") return `Confirmo o recebimento integral de ${amountLabel(action.invoice.amount)} em dinheiro, hoje, para esta cobrança.`;
  if (action.kind === "create") return "Confirmo a emissão da mensalidade para o associado e competência indicados, conforme valor institucional validado pelo servidor.";
  return "Confirmo a alteração desta cobrança não quitada e a preservação do lançamento original no histórico.";
}

function OperationForm({ action, busy, onCancel, onSubmit }: { action: Action; busy: boolean; onCancel(): void; onSubmit(values: FormValues): void }) {
  const [values, setValues] = useState<FormValues>(() => ({ competence: defaultCompetence(), dueDate: action.kind === "create" ? `${defaultCompetence()}-10` : action.invoice.due_date, method: "PIX", justification: "" }));
  const [confirmed, setConfirmed] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => { heading.current?.focus(); }, []);
  const needsJustification = ["cancel", "replace"].includes(action.kind);
  const validJustification = !needsJustification || values.justification.trim().length > 0;
  const canSubmit = confirmed && validJustification;
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!busy && canSubmit) onSubmit(values);
  }
  return <section className="member-finance__operation" aria-labelledby="finance-operation-title">
    <h2 id="finance-operation-title" tabIndex={-1} ref={heading}>{actionTitles[action.kind]}</h2>
    {action.kind !== "create" && <p>{invoiceLabel(action.invoice)} · <strong>{amountLabel(action.invoice.amount)}</strong></p>}
    <p>{action.kind === "cash" ? "Somente dinheiro e valor integral. Não registre parcelas, troco ou transferência manual." : "O servidor valida o estado da cobrança e a política institucional antes de concluir."}</p>
    <form onSubmit={submit}>
      <fieldset className="member-finance__fieldset" disabled={busy}>
        <legend>Conferência da operação</legend>
        {(action.kind === "create" || action.kind === "replace") && <DigitalFields values={values} onChange={(value) => { setValues(value); setConfirmed(false); }} creating={action.kind === "create"} />}
        {needsJustification && <div className="field"><label className="field__label" htmlFor="finance-justification">Justificativa</label><input id="finance-justification" aria-describedby="finance-justification-hint" className="field__input" required maxLength={500} value={values.justification} onChange={(event) => { setValues({ ...values, justification: event.target.value }); setConfirmed(false); }} /><small id="finance-justification-hint" className="field__hint">Descreva o motivo sem dados sensíveis. O histórico original será preservado.</small></div>}
        <label className="member-finance__confirmation"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>{confirmationLabel(action)}</span></label>
        <div className="member-finance__actions"><button className="btn btn--primary" disabled={!canSubmit || busy} type="submit">{busy ? "Processando…" : confirmationTitles[action.kind]}</button><button className="btn btn--secondary" type="button" onClick={onCancel}>Voltar sem alterar</button></div>
      </fieldset>
    </form>
  </section>;
}

async function executeOperation(accessToken: string, memberId: string, action: Action, values: FormValues, operationId: string): Promise<AssistedPaymentResponse | null> {
  if (action.kind === "create") {
    await createPayment(accessToken, { kind: "monthly_fee", member_id: memberId, competencia: `${values.competence}-01`, due_date: values.dueDate, billing_type: values.method });
    return null;
  }
  const input = { payment_id: action.invoice.id, operation_id: operationId };
  if (action.kind === "cash") return recordCashPayment(accessToken, { ...input, confirmation: "cash_received_in_full" });
  if (action.kind === "cancel") return cancelPayment(accessToken, { ...input, justification: values.justification.trim() });
  return replacePayment(accessToken, { ...input, justification: values.justification.trim(), due_date: values.dueDate, billing_type: values.method });
}

function resultFeedback(result: AssistedPaymentResponse | null): Feedback {
  if (!result) return { tone: "success", message: "Cobrança digital emitida. Protocolo não disponibilizado pelo serviço de emissão; confira o registro no histórico. As instruções ficam no acesso do titular." };
  const labels = { completed: "Operação concluída", processing: "Operação em processamento; não reenvie", reconciliation_required: "Reconciliação necessária; não reenvie" };
  return { tone: result.status === "completed" ? "success" : "warning", message: `${labels[result.status]}. Protocolo ${result.protocol_number}.` };
}

function canRetryAfterRefresh(error: unknown): boolean {
  return error instanceof ApiError && [400, 409].includes(error.status);
}

function sessionHasExpired(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}

function ReadyFinance({ overview, capabilities, busy, locked, action, onAction, onSubmit }: {
  overview: FinancialOverview;
  capabilities: MemberFinanceOperationsProps["capabilities"];
  busy: boolean;
  locked: boolean;
  action: Action | null;
  onAction(action: Action | null): void;
  onSubmit(values: FormValues): void;
}) {
  const allowedAction = action && mayPerform(action, capabilities) ? action : null;
  const canIssue = !allowedAction && !locked && capabilities.recordPayment && !overview.invoices.some(isActive);
  return <>
    <FinancialLedger overview={overview} capabilities={capabilities} disabled={busy || locked || allowedAction !== null} onAction={onAction} />
    {canIssue && <section className="member-finance__next"><h2>Próximo atendimento</h2><p>Sem cobrança ativa. A mensalidade segue o valor institucional, sem edição de valor no balcão.</p><button type="button" className="btn btn--primary" disabled={busy} onClick={() => onAction({ kind: "create" })}>Gerar cobrança digital</button></section>}
    {allowedAction && <OperationForm action={allowedAction} busy={busy} onCancel={() => onAction(null)} onSubmit={onSubmit} />}
  </>;
}

function OperationFeedback({ feedback }: { feedback: Feedback }) {
  const location = useLocation();
  const node = useRef<HTMLDivElement>(null);
  useEffect(() => { node.current?.focus(); }, [feedback]);
  return <div ref={node} tabIndex={-1} className={`status-banner status-banner--${feedback.tone}`} role={feedback.tone === "success" ? "status" : "alert"} aria-live={feedback.tone === "success" ? "polite" : "assertive"}>{feedback.message}{feedback.mfa && <Link className="member-finance__back" to={`/mfa/desafio?retorno=${encodeURIComponent(location.pathname)}`}>Confirmar autenticação MFA</Link>}</div>;
}

function AttendedOperations({ memberId, accessToken, capabilities, onSessionExpired }: MemberFinanceOperationsProps) {
  const [overview, setOverview] = useState<FinancialOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState<Action | null>(null);
  const [busy, setBusy] = useState(false);
  const [locked, setLocked] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const canRefreshConflict = useRef(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setOverview(null);
    try {
      const next = await getMemberFinancialOverview(accessToken, memberId);
      if (alive.current) setOverview(next);
      return true;
    } catch (error: unknown) {
      if (!alive.current) return;
      if (sessionHasExpired(error)) { onSessionExpired(); return; }
      setFeedback({ tone: "error", message: "Não foi possível consultar o financeiro. Verifique a conexão e sua permissão antes de continuar." });
      return false;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [accessToken, memberId, onSessionExpired]);

  useEffect(() => {
    alive.current = true;
    void loadOverview();
    return () => { alive.current = false; };
  }, [loadOverview]);

  async function submit(values: FormValues) {
    if (!action || inFlight.current || !mayPerform(action, capabilities)) return;
    inFlight.current = true;
    setBusy(true);
    setFeedback(null);
    try {
      const result = await executeOperation(accessToken, memberId, action, values, crypto.randomUUID());
      if (!alive.current) return;
      setLocked(result !== null && result.status !== "completed");
      setFeedback(resultFeedback(result));
      setAction(null);
      await loadOverview();
    } catch (error: unknown) {
      if (!alive.current) return;
      if (sessionHasExpired(error)) { onSessionExpired(); return; }
      setLocked(true);
      canRefreshConflict.current = canRetryAfterRefresh(error);
      setFeedback(failureFeedback(error));
      setAction(null);
    } finally {
      inFlight.current = false;
      if (alive.current) setBusy(false);
    }
  }

  async function refresh() {
    setAction(null);
    const refreshed = await loadOverview();
    if (refreshed && canRefreshConflict.current) {
      setLocked(false);
      canRefreshConflict.current = false;
      setFeedback(null);
    }
  }

  return <div className="member-finance__body" aria-busy={busy}>
    {feedback && <OperationFeedback feedback={feedback} />}
    {loading && !feedback && <p role="status" aria-live="polite">Consultando histórico financeiro…</p>}
    <div className="member-finance__toolbar"><p>Consulte o histórico antes de iniciar ou repetir um atendimento.</p><button type="button" className="btn btn--secondary" disabled={loading || busy} onClick={() => void refresh()}>Atualizar histórico</button></div>
    {!loading && overview && <ReadyFinance overview={overview} capabilities={capabilities} busy={busy} locked={locked} action={action} onAction={setAction} onSubmit={(values) => void submit(values)} />}
  </div>;
}

export function MemberFinanceOperations(props: MemberFinanceOperationsProps) {
  if (!props.capabilities.readPayments) return <p role="alert">Sem permissão para consultar este financeiro.</p>;
  return <AttendedOperations key={`${props.memberId}:${props.accessToken}`} {...props} />;
}
